/**
 * Deterministic lockstep coordination for turn-based online battles.
 *
 * Model (design D5): every client runs the same deterministic simulation from the same init and seed. At any tick
 * exactly one team owns the input stream: the team of the current turn of the command log. The owner's client records
 * its input (`recordLocal`), batches it and relays it through the backend (`LockstepLink`) together with
 * `throughTick` - the last tick whose input is final. Other clients may only simulate a tick once its input is known
 * (`canAdvance` / `frameAt`).
 *
 * Turns follow the simulation, never a clock. Whenever the simulation starts a new move (proc.new_move) every client
 * tells its coordinator (`onMoveStart(tick, team)`, after the input of that tick was recorded):
 *  - the owner of the current turn ends it there when the move belongs to another team: the turn's last tick is the
 *    tick in which the new move started (the owner provided its input) and the next turn starts after it
 *    (session.endTurn { turn, tick, nextTeam }; the server broadcasts the change);
 *  - the other clients have nothing to send; the turn change reaches them as an event and is checked against their
 *    own move starts (a difference is a 'mismatch': the simulations diverged or a participant lied).
 * Forced turn: when the owner misses its deadline, disconnects beyond the grace period or forfeits, the server closes
 * the turn's input after its confirmed tick E (event 'inputClosed') WITHOUT choosing the next team, because only the
 * simulation knows it. Every client simulates the rest of that move with EMPTY input until its own simulation starts
 * the next move at T'; the client that controls the team of that move reports it (session.endTurn with
 * `forcedEnd: E`); the server accepts the first well-formed report and broadcasts the turn change. An owner whose
 * simulation already used input the server dropped (recorded after E) must rebuild the battle from the log
 * ('rebuild'); recorded empty frames after E need no rebuild, they are what the forced turn means anyway.
 *
 * State hashes (`checkpoint`) are compared by the backend; a mismatch arrives as a `desync` event.
 *
 * Reliability: batches are contiguous (`fromTick`). The owner keeps an outbox and re-sends the whole unacknowledged
 * range after a failure (the backend skips what it already has); receivers detect gaps (a lost event, a reconnect) and
 * resynchronize from the backend's log (`resync`, automatic when the link provides `fetchLog`). Turn ends and reports
 * are delivered in turn order, before the input of a later turn.
 *
 * The coordinator is transport-agnostic: `LockstepLink` is implemented by the client backend layer
 * (src/client/net/lockstep-link.ts) on top of any Transport, and by tests directly.
 */
import { CommandLog, type CommandLogData, type TurnRecord } from './command-log';
import { type InputCommand, type InputFrame, EMPTY_FRAME, cloneFrame, framesEqual, isEmptyFrame, isOneShotFrame } from './input';
import { type StateHasher, defaultStateHasher } from './hash';

export interface LockstepInputBatch {
  turn: number;
  /** First tick covered by this batch (previous confirmed tick + 1). */
  fromTick: number;
  throughTick: number;
  commands: InputCommand[];
}

export interface LockstepTurnChange {
  /** Index of the turn that ended. */
  endedTurn: number;
  /** Last tick of the ended turn (the tick in which the simulation started the next move). */
  endTick: number;
  /** Index of the new turn (`endedTurn + 1`). */
  turn: number;
  /** Team owning the new turn. */
  team: number;
  /** True when the ended turn had been forced (its end was reported after the server closed its input). */
  forced: boolean;
  /** Wall-clock deadline (epoch ms) for the new turn, if the backend enforces one. */
  deadlineAt?: number;
}

/** The server closed the input of a turn after `tick` (a forced turn): its later ticks are empty frames. */
export interface LockstepInputClosed {
  turn: number;
  tick: number;
  /** Until when the server waits for the report of the turn's end (epoch ms), if it says. */
  deadlineAt?: number;
}

export interface LockstepDesync {
  tick: number;
  /** Hash per participant (player id), as collected by the backend. */
  hashes: Record<string, string>;
}

/** The local simulation used input the server does not have: rebuild the battle from the log (fast-forward). */
export interface LockstepRebuild {
  /** First tick whose input differs from what the simulation used. */
  fromTick: number;
  reason: string;
}

/** A turn change disagrees with the local simulation's move starts (a divergence or a lying participant). */
export interface LockstepMismatch {
  tick: number;
  reason: string;
}

export type LockstepEvent =
  | { type: 'input'; team: number; batch: LockstepInputBatch }
  | { type: 'turn'; change: LockstepTurnChange }
  | { type: 'inputClosed'; closed: LockstepInputClosed }
  | { type: 'desync'; desync: LockstepDesync }
  | { type: 'ended'; winnerTeam: number; verified: boolean; reason?: string };

/** A turn end the coordinator delivers: the owner's end, or the report of a forced turn's end (`forcedEnd`). */
export interface LockstepEndTurn {
  turn: number;
  tick: number;
  nextTeam: number;
  /** Report of a forced turn: the tick after which the server closed the turn's input. */
  forcedEnd?: number;
}

/** Relay used by the coordinator. All methods resolve once the backend accepted the message. */
export interface LockstepLink {
  /** Resolves with the backend's confirmed tick of the turn. */
  sendInput(batch: LockstepInputBatch): Promise<{ throughTick: number }>;
  sendEndTurn(req: LockstepEndTurn): Promise<void>;
  sendHash(req: { tick: number; hash: string; algorithm: string }): Promise<void>;
  subscribe(listener: (event: LockstepEvent) => void): () => void;
  /** The backend's current command log (enables automatic resync after a detected gap). */
  fetchLog?(): Promise<CommandLogData>;
}

export interface LockstepOptions {
  /** Teams controlled by this client. */
  localTeams: readonly number[];
  /** Team owning turn 0 (the original always starts with team 1, proc.as:769). */
  firstTeam: number;
  link: LockstepLink;
  hasher?: StateHasher;
  /** Send a batch at least every N recorded ticks (default 5 = 100 ms at 50 Hz). */
  flushEveryTicks?: number;
  startTick?: number;
  /** Resume from the backend's log (e.g. after a reload/reconnect: `session.get`). */
  initialLog?: CommandLogData;
  /** Delay before re-sending after a failed send (default 200 ms). */
  retryDelayMs?: number;
  /** Consecutive INTERNAL (server-side) failures re-sent before the pump halts (default 5). */
  maxInternalRetries?: number;
}

/**
 * Failures of a send that re-sending can fix: transport failures (no protocol code), an unavailable, overloaded or
 * failing (INTERNAL, bounded) backend, and a CONFLICT that tells where the backend's confirmed input ends.
 * Everything else (NOT_FOUND after the session is gone, FORBIDDEN, VALIDATION_FAILED, UNAUTHORIZED, ...) halts the pump.
 */
function sendRetryable(err: unknown, backendBehind: boolean): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  if (typeof code !== 'string') return true;
  if (code === 'UNAVAILABLE' || code === 'RATE_LIMITED' || code === 'INTERNAL') return true;
  return code === 'CONFLICT' && backendBehind;
}

/** The backend refused input or a turn end because it forced the turn meanwhile (CONFLICT 'turn_forced'). */
function turnForcedError(err: unknown): { forcedAt: number | null } | null {
  const e = err as { code?: unknown; details?: { reason?: unknown; forcedAt?: unknown } } | null;
  if (e?.code !== 'CONFLICT' || e.details?.reason !== 'turn_forced') return null;
  return { forcedAt: typeof e.details.forcedAt === 'number' ? e.details.forcedAt : null };
}

/** The turn has input (a command) after `tick`. */
function hasInputAfter(t: TurnRecord, tick: number): boolean {
  return t.commands.some((c) => c.tick + c.repeat > tick);
}

export interface LockstepGap {
  reason: string;
}

type Listener<T> = (value: T) => void;
type Ended = { winnerTeam: number; verified: boolean; reason?: string };

export class LockstepCoordinator {
  readonly log: CommandLog;
  readonly hasher: StateHasher;
  readonly desyncs: LockstepDesync[] = [];
  readonly mismatches: LockstepMismatch[] = [];
  /** Local checkpoint hashes by tick. */
  readonly localHashes = new Map<number, string>();
  ended: Ended | null = null;

  private readonly localTeams: ReadonlySet<number>;
  private readonly link: LockstepLink;
  private readonly flushEvery: number;
  private readonly retryDelay: number;
  // Local recording that is not in the log yet.
  private pending: InputCommand[] = [];
  private run: InputCommand | null = null;
  private recordedThrough: number;
  private lastFlushTick: number;
  // Outbox: the local turn whose input is being delivered and how far the backend confirmed it.
  private outTurn: number | null = null;
  private ackedThrough = -1;
  /** Turn ends (and reports of forced turns' ends) not yet accepted by the backend, by turn. */
  private readonly pendingEnds = new Map<number, LockstepEndTurn>();
  /** Move starts of the local simulation (tick -> team) not yet checked against a closed turn. */
  private readonly moveStarts = new Map<number, number>();
  private pumping: Promise<void> | null = null;
  private repump = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private resyncing: Promise<void> | null = null;
  private hashChain: Promise<void> = Promise.resolve();
  private disposed = false;
  private unsubscribe: (() => void) | null;
  private listeners = {
    turn: new Set<Listener<LockstepTurnChange>>(),
    inputClosed: new Set<Listener<LockstepInputClosed>>(),
    desync: new Set<Listener<LockstepDesync>>(),
    input: new Set<Listener<{ team: number; batch: LockstepInputBatch }>>(),
    ended: new Set<Listener<Ended>>(),
    gap: new Set<Listener<LockstepGap>>(),
    rebuild: new Set<Listener<LockstepRebuild>>(),
    mismatch: new Set<Listener<LockstepMismatch>>(),
    error: new Set<Listener<Error>>(),
    halted: new Set<Listener<Error>>(),
  };
  private halt: Error | null = null;
  private internalFailures = 0;
  private readonly maxInternalRetries: number;

  constructor(opts: LockstepOptions) {
    const start = opts.startTick ?? 0;
    this.log = opts.initialLog ? CommandLog.fromJSON(opts.initialLog) : CommandLog.create(opts.firstTeam, start);
    this.localTeams = new Set(opts.localTeams);
    this.link = opts.link;
    this.hasher = opts.hasher ?? defaultStateHasher;
    this.flushEvery = Math.max(1, opts.flushEveryTicks ?? 5);
    this.retryDelay = opts.retryDelayMs ?? 200;
    this.maxInternalRetries = opts.maxInternalRetries ?? 5;
    this.recordedThrough = this.log.current.throughTick;
    this.lastFlushTick = this.recordedThrough;
    this.adoptOutbox();
    this.unsubscribe = this.link.subscribe((e) => this.handleEvent(e));
  }

  get turn(): number {
    return this.log.current.turn;
  }

  get currentTeam(): number {
    return this.log.current.team;
  }

  /** Last tick of the local turn recorded so far (the log's confirmed tick outside a local turn). */
  get recordedTick(): number {
    return this.recordedThrough;
  }

  isLocalTeam(team: number): boolean {
    return this.localTeams.has(team);
  }

  /** The current turn is this client's and takes input (not forced). */
  isLocalTurn(): boolean {
    const cur = this.log.current;
    return this.localTeams.has(cur.team) && cur.endTick === null && cur.forcedAt === undefined;
  }

  /** The server closed the current turn's input (a forced turn whose end is not reported yet). */
  isInputClosed(): boolean {
    const cur = this.log.current;
    return cur.forcedAt !== undefined && cur.endTick === null;
  }

  /** True while recorded local input or a turn end has not been confirmed by the backend. */
  hasUnsent(): boolean {
    if (this.pending.length || this.run || this.pendingEnds.size) return true;
    if (this.outTurn === null) return false;
    const t = this.log.turns[this.outTurn];
    return !!t && t.forcedAt === undefined && t.throughTick > this.ackedThrough;
  }

  on(event: 'turn', cb: Listener<LockstepTurnChange>): () => void;
  on(event: 'inputClosed', cb: Listener<LockstepInputClosed>): () => void;
  on(event: 'desync', cb: Listener<LockstepDesync>): () => void;
  on(event: 'input', cb: Listener<{ team: number; batch: LockstepInputBatch }>): () => void;
  on(event: 'ended', cb: Listener<Ended>): () => void;
  on(event: 'gap', cb: Listener<LockstepGap>): () => void;
  /** The local simulation used input the server dropped: rebuild the battle from the log. */
  on(event: 'rebuild', cb: Listener<LockstepRebuild>): () => void;
  /** A turn change disagrees with the local simulation's move starts. */
  on(event: 'mismatch', cb: Listener<LockstepMismatch>): () => void;
  on(event: 'error', cb: Listener<Error>): () => void;
  /** The pump stopped on a failure that re-sending cannot fix (see `failure`, `resume`). */
  on(event: 'halted', cb: Listener<Error>): () => void;
  on(event: keyof LockstepCoordinator['listeners'], cb: Listener<any>): () => void {
    const set = this.listeners[event] as Set<Listener<any>>;
    set.add(cb);
    return () => set.delete(cb);
  }

  // ------------------------------------------------------------------------------------------------------
  // Owner side

  /**
   * Records the local owner's input for `tick` (call once per simulation tick while it is the local turn;
   * empty frames advance `throughTick`). One-shot frames (weapon, click, scanner, surrender) are sent immediately.
   */
  recordLocal(tick: number, frame: InputFrame): void {
    if (!this.isLocalTurn()) throw new Error(`recordLocal: turn ${this.turn} of team ${this.currentTeam} takes no local input`);
    const cur = this.log.current;
    if (tick < cur.startTick) throw new Error(`recordLocal: tick ${tick} precedes turn start ${cur.startTick}`);
    if (tick <= this.recordedThrough) throw new Error(`recordLocal: tick ${tick} already recorded`);
    const f = cloneFrame(frame);
    const immediate = isOneShotFrame(f);
    if (isEmptyFrame(f)) {
      this.closeRun();
    } else if (immediate) {
      this.closeRun();
      this.pending.push({ tick, frame: f, repeat: 0 });
    } else if (this.run && framesEqual(this.run.frame, f) && this.run.tick + this.run.repeat === tick - 1) {
      this.run.repeat++;
    } else {
      this.closeRun();
      this.run = { tick, frame: f, repeat: 0 };
    }
    this.recordedThrough = tick;
    if (immediate || tick - this.lastFlushTick >= this.flushEvery) void this.flush();
  }

  /** Moves recorded input into the log and delivers everything unsent. Never rejects (failures: 'error'). */
  flush(): Promise<void> {
    this.commitRecorded();
    return this.pump();
  }

  private commitRecorded(): void {
    if (!this.isLocalTurn()) return;
    this.closeRun();
    const cur = this.log.current;
    if (this.recordedThrough <= cur.throughTick && this.pending.length === 0) return;
    const err = this.log.append(cur.turn, this.pending, Math.max(this.recordedThrough, cur.throughTick));
    this.pending = [];
    this.lastFlushTick = this.recordedThrough;
    if (err) this.emit('error', new Error(`lockstep: local input rejected: ${err}`));
  }

  /**
   * Ends the local turn at `tick` (the last tick of this turn: the tick in which the simulation started the next
   * move) and hands the input stream to `nextTeam`. Games call onMoveStart, which calls this when it applies.
   */
  async endLocalTurn(tick: number, nextTeam: number): Promise<void> {
    if (!this.isLocalTurn()) throw new Error(`endLocalTurn: turn ${this.turn} is not local`);
    if (tick < this.recordedThrough) throw new Error(`endLocalTurn: tick ${tick} precedes recorded tick ${this.recordedThrough}`);
    this.recordedThrough = tick;
    this.commitRecorded();
    const turn = this.turn;
    const err = this.log.endTurn(turn, tick, nextTeam);
    if (err) throw new Error(`endLocalTurn: ${err}`);
    this.pendingEnds.set(turn, { turn, tick, nextTeam });
    this.recordedThrough = tick;
    this.lastFlushTick = tick;
    this.settleMoves(tick);
    this.emit('turn', { endedTurn: turn, endTick: tick, turn: turn + 1, team: nextTeam, forced: false });
    await this.pump();
  }

  /**
   * The local simulation started a new move of `team` in `tick` (proc.new_move; call it after the tick's input was
   * recorded). Ends the local turn when the move belongs to another team, reports the end of a forced turn when the
   * move's team is local, and checks the move against the turn changes known so far (a difference: 'mismatch').
   * Calls for moves the coordinator already knows (a rebuild re-simulating the battle) only check them.
   */
  async onMoveStart(tick: number, team: number): Promise<void> {
    const cur = this.log.current;
    if (tick < cur.startTick) {
      this.checkMove(tick, team);
      return;
    }
    this.moveStarts.set(tick, team);
    if (cur.forcedAt !== undefined) {
      if (team === cur.team) return;
      if (tick >= cur.forcedAt) this.maybeReportForced();
      else this.mismatch(tick, `the simulation started a move of team ${team} in tick ${tick}, inside the forced turn's input (through ${cur.forcedAt})`);
      return;
    }
    if (this.isLocalTurn() && team !== cur.team && tick >= this.recordedThrough) await this.endLocalTurn(tick, team);
  }

  /**
   * The next move after the current forced turn's closed input as the local simulation started it (the first move
   * start of another team at or after the closed tick), or null while the simulation has not reached it.
   */
  private forcedNextMove(): { tick: number; team: number } | null {
    const cur = this.log.current;
    if (cur.forcedAt === undefined || cur.endTick !== null) return null;
    let first: number | null = null;
    for (const [tick, team] of this.moveStarts) {
      if (tick >= cur.forcedAt && tick >= cur.startTick - 1 && team !== cur.team && (first === null || tick < first)) first = tick;
    }
    return first === null ? null : { tick: first, team: this.moveStarts.get(first)! };
  }

  /** Checks the local move starts up to `tick` (now inside finished turns) and forgets them. */
  private settleMoves(tick: number): void {
    for (const [t, team] of [...this.moveStarts]) if (t <= tick) this.checkMove(t, team);
  }

  /** Reports the end of the current forced turn when the local simulation started the next move of a local team. */
  private maybeReportForced(): void {
    const cur = this.log.current;
    if (cur.forcedAt === undefined || cur.endTick !== null || this.pendingEnds.has(cur.turn)) return;
    const next = this.forcedNextMove();
    if (!next || !this.localTeams.has(next.team)) return;
    const turn = cur.turn;
    const forcedEnd = cur.forcedAt;
    const err = this.log.endTurn(turn, next.tick, next.team);
    if (err) {
      this.emit('error', new Error(`lockstep: forced turn end rejected: ${err}`));
      return;
    }
    this.pendingEnds.set(turn, { turn, tick: next.tick, nextTeam: next.team, forcedEnd });
    this.recordedThrough = next.tick;
    this.lastFlushTick = next.tick;
    this.settleMoves(next.tick);
    this.emit('turn', { endedTurn: turn, endTick: next.tick, turn: turn + 1, team: next.team, forced: true });
    void this.pump();
  }

  private adoptOutbox(): void {
    if (this.outTurn === null && this.isLocalTurn()) {
      this.outTurn = this.log.current.turn;
      this.ackedThrough = this.log.current.throughTick;
    }
  }

  /** The failure that halted the delivery of local input (null while delivering normally). */
  get failure(): Error | null {
    return this.halt;
  }

  /** Restarts a halted delivery (e.g. after the host re-authenticated); unsent input is sent again. */
  resume(): Promise<void> {
    this.halt = null;
    this.internalFailures = 0;
    return this.pump();
  }

  /** Delivers unsent input and turn ends in order; on failure schedules a retry of the whole range. */
  private pump(): Promise<void> {
    if (this.disposed || this.halt) return Promise.resolve();
    if (this.pumping) {
      this.repump = true;
      return this.pumping;
    }
    this.pumping = (async () => {
      try {
        do {
          this.repump = false;
          await this.pumpLoop();
          // A halt ends the delivery even when more input arrived while the failing request was out.
        } while (this.repump && !this.disposed && !this.halt);
      } finally {
        this.pumping = null;
      }
    })();
    return this.pumping;
  }

  private firstPendingEnd(): number | null {
    let first: number | null = null;
    for (const turn of this.pendingEnds.keys()) if (first === null || turn < first) first = turn;
    return first;
  }

  private async pumpLoop(): Promise<void> {
    for (;;) {
      if (this.disposed || this.halt) return;
      if (this.outTurn !== null) {
        // A turn whose input is delivered (or closed by the backend) and whose end is settled leaves the outbox.
        const t = this.log.turns[this.outTurn];
        if (!t || (t.endTick !== null && (t.forcedAt !== undefined || t.throughTick <= this.ackedThrough) && !this.pendingEnds.has(t.turn))) this.outTurn = null;
      }
      this.adoptOutbox();
      const out = this.outTurn !== null ? this.log.turns[this.outTurn] : undefined;
      const endTurn = this.firstPendingEnd();
      const inputDue = !!out && out.forcedAt === undefined && out.throughTick > this.ackedThrough && (endTurn === null || out.turn <= endTurn);
      if (!inputDue && endTurn === null) return;
      let sending: 'input' | 'end' = 'input';
      try {
        if (inputDue) {
          const t = out!;
          const from = this.ackedThrough;
          const batch: LockstepInputBatch = {
            turn: t.turn,
            fromTick: from + 1,
            throughTick: t.throughTick,
            commands: t.commands.filter((c) => c.tick > from).map((c) => ({ tick: c.tick, frame: cloneFrame(c.frame), repeat: c.repeat })),
          };
          const res = await this.link.sendInput(batch);
          this.internalFailures = 0;
          if (this.outTurn === t.turn) this.ackedThrough = Math.max(this.ackedThrough, Math.min(res.throughTick, batch.throughTick));
          continue;
        }
        sending = 'end';
        const end = this.pendingEnds.get(endTurn!)!;
        await this.link.sendEndTurn({ ...end });
        this.internalFailures = 0;
        if (this.pendingEnds.get(endTurn!) === end) this.pendingEnds.delete(endTurn!);
        if (this.outTurn !== null && this.outTurn <= endTurn!) {
          const next = this.log.turns[endTurn! + 1];
          if (next && this.localTeams.has(next.team) && next.endTick === null && next.forcedAt === undefined) {
            this.outTurn = next.turn;
            this.ackedThrough = next.startTick - 1;
          } else this.outTurn = null;
        }
        continue;
      } catch (err) {
        const forced = turnForcedError(err);
        if (forced) {
          // The backend forced the turn before this arrived: its 'inputClosed' event (or a resync) settles it.
          this.emit('error', err instanceof Error ? err : new Error(String(err)));
          if (sending === 'end' && endTurn !== null) this.onEndRefusedForced(endTurn, forced.forcedAt);
          else if (out) {
            if (this.outTurn === out.turn) this.outTurn = null;
            if (out.forcedAt === undefined) this.triggerResync('the backend forced the local turn');
          }
          continue;
        }
        const details = (err as { details?: { throughTick?: number } }).details;
        // The backend is behind us (e.g. a lost request): restart the range from its confirmed tick.
        const behind = sending === 'input' && (err as { code?: string }).code === 'CONFLICT' && typeof details?.throughTick === 'number' && details.throughTick < this.ackedThrough;
        if (behind) this.ackedThrough = details!.throughTick!;
        const error = err instanceof Error ? err : new Error(String(err));
        this.emit('error', error);
        const internal = (err as { code?: string }).code === 'INTERNAL';
        this.internalFailures = internal ? this.internalFailures + 1 : 0;
        if (!sendRetryable(err, behind) || (internal && this.internalFailures > this.maxInternalRetries)) {
          // Re-sending cannot fix this: stop instead of retrying every retryDelayMs forever, and tell the host.
          this.halt = error;
          this.emit('halted', error);
          return;
        }
        this.scheduleRetry();
        return;
      }
    }
  }

  /**
   * Our end of `turn` was refused because the backend had forced the turn: when the turn's input was closed at our
   * end tick or before it with nothing but empty input after it, our end is exactly the forced turn's end - reported
   * by us when its next team is local (else by that team's owner); otherwise our simulation used input the backend
   * dropped (resync + rebuild).
   */
  private onEndRefusedForced(turn: number, forcedAt: number | null): void {
    const end = this.pendingEnds.get(turn);
    if (!end || end.forcedEnd !== undefined) {
      this.pendingEnds.delete(turn);
      return;
    }
    const t = this.log.turns[turn];
    if (forcedAt === null || !t || end.tick < forcedAt || hasInputAfter(t, forcedAt)) {
      this.pendingEnds.delete(turn);
      this.triggerResync('the backend forced a turn this client had ended', true);
      return;
    }
    this.markForced(turn, forcedAt);
    if (this.localTeams.has(end.nextTeam)) end.forcedEnd = forcedAt;
    else this.pendingEnds.delete(turn);
  }

  /** Marks the (locally closed) turn as forced after `tick`, as the backend's log has it (no input after it). */
  private markForced(turn: number, tick: number): void {
    const t = this.log.turns[turn];
    if (t && t.forcedAt === undefined) t.forcedAt = tick;
  }

  private scheduleRetry(): void {
    if (this.retryTimer || this.disposed) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.pump();
    }, this.retryDelay);
  }

  // ------------------------------------------------------------------------------------------------------
  // Consumer side

  /** True when the input of `tick` is known (local turn ticks are always known up to the recorded tick). */
  canAdvance(tick: number): boolean {
    return this.frameAt(tick) !== null;
  }

  /**
   * Input for `tick`: the frame, an empty frame when the tick has no input (also every tick of a forced turn after its
   * closed input), or `null` when the simulation must wait for the owner's input to arrive.
   */
  frameAt(tick: number): InputFrame | null {
    const cur = this.log.current;
    if (cur.forcedAt !== undefined && cur.endTick === null && tick > cur.forcedAt) {
      // The rest of a forced move is empty up to the tick in which the local simulation started the next move;
      // the ticks after it wait for the reported turn change (they belong to the next team).
      const next = this.forcedNextMove();
      if (next && tick > next.tick) return null;
    }
    const known = this.log.frameAt(tick);
    if (known) return known;
    if (!this.isLocalTurn() || tick < cur.startTick || tick > this.recordedThrough) return null;
    for (const c of this.pending) if (tick >= c.tick && tick <= c.tick + c.repeat) return cloneFrame(c.frame);
    if (this.run && tick >= this.run.tick && tick <= this.run.tick + this.run.repeat) return cloneFrame(this.run.frame);
    return { ...EMPTY_FRAME };
  }

  /** Computes (unless a hash string is given) and reports the state hash for `tick`. */
  async checkpoint(tick: number, stateOrHash: unknown): Promise<string> {
    const hash = typeof stateOrHash === 'string' ? stateOrHash : this.hasher.hash(stateOrHash);
    this.localHashes.set(tick, hash);
    const sent = this.hashChain.then(() => this.link.sendHash({ tick, hash, algorithm: this.hasher.algorithm }));
    this.hashChain = sent.catch((err) => this.emit('error', err instanceof Error ? err : new Error(String(err))));
    await this.hashChain;
    return hash;
  }

  /** Waits until everything recorded so far was delivered (or the last attempt failed and a retry is queued). */
  async idle(): Promise<void> {
    this.commitRecorded();
    await this.pump();
    await this.hashChain;
  }

  /**
   * Adopts the backend's log when it is ahead of ours (recovery after a reconnect or a lost event), or - with
   * `replace` - whenever it differs (our log holds input or a turn end the backend dropped). Returns true when the
   * local log was replaced.
   */
  resync(server: CommandLogData, opts: { replace?: boolean } = {}): boolean {
    const remote = CommandLog.fromJSON(server);
    const rc = remote.current;
    const lc = this.log.current;
    const ahead =
      rc.turn > lc.turn ||
      (rc.turn === lc.turn && (rc.throughTick > lc.throughTick || (rc.forcedAt !== undefined && lc.forcedAt === undefined) || (rc.endTick !== null && lc.endTick === null)));
    const differs = opts.replace === true && JSON.stringify(remote.toJSON()) !== JSON.stringify(this.log.toJSON());
    if (!ahead && !differs) return false;
    // Input our simulation may have used that the backend does not have (e.g. recorded after a forced close).
    this.commitRecorded();
    const firstDiff = this.firstDifference(remote);
    if (firstDiff !== null) this.emit('rebuild', { fromTick: firstDiff, reason: 'the local log holds input the backend does not have' });
    (this.log.turns as unknown[]).splice(0, this.log.turns.length, ...remote.turns);
    this.pending = [];
    this.run = null;
    this.recordedThrough = this.log.current.throughTick;
    this.lastFlushTick = this.recordedThrough;
    for (const turn of [...this.pendingEnds.keys()]) if (turn < this.log.current.turn || this.log.turns[turn]?.endTick !== null) this.pendingEnds.delete(turn);
    this.outTurn = null;
    this.adoptOutbox();
    this.settleMoves(this.log.current.startTick - 1);
    this.maybeReportForced();
    void this.pump();
    return true;
  }

  /**
   * First tick whose input differs between our log and `remote`, as far as our log knows input (null when they
   * agree): a tick the backend does not know yet differs only when our frame is not empty.
   */
  private firstDifference(remote: CommandLog): number | null {
    const start = this.log.turns[0]?.startTick ?? 0;
    const end = Math.max(this.recordedThrough, this.log.current.throughTick);
    for (let tick = Math.max(0, start); tick <= end; tick++) {
      const a = this.frameAt(tick);
      if (!a) continue;
      const b = remote.frameAt(tick);
      if (!b ? !isEmptyFrame(a) : !framesEqual(a, b)) return tick;
    }
    return null;
  }

  private triggerResync(reason: string, replace = false): void {
    this.emit('gap', { reason });
    if (!this.link.fetchLog || this.resyncing || this.disposed) return;
    this.resyncing = this.link
      .fetchLog()
      .then((log) => {
        this.resync(log, { replace });
      })
      .catch((err) => this.emit('error', err instanceof Error ? err : new Error(String(err))))
      .finally(() => (this.resyncing = null));
  }

  /** Resolves when a running automatic resync has finished. */
  async settled(): Promise<void> {
    while (this.resyncing) await this.resyncing;
  }

  dispose(): void {
    this.disposed = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  /** Applies a backend event (normally called through the link subscription). */
  handleEvent(e: LockstepEvent): void {
    switch (e.type) {
      case 'input': {
        if (this.localTeams.has(e.team)) return; // our own input (the backend does not echo it)
        const b = e.batch;
        const cur = this.log.current;
        if (b.turn < cur.turn || (b.turn === cur.turn && b.throughTick <= cur.throughTick)) return; // stale / duplicate
        if (b.turn > cur.turn) return this.triggerResync(`input for turn ${b.turn} while at turn ${cur.turn}`);
        if (b.fromTick > cur.throughTick + 1) return this.triggerResync(`input gap after tick ${cur.throughTick}`);
        const fresh = b.commands.filter((c) => c.tick > cur.throughTick);
        const err = this.log.append(b.turn, fresh, b.throughTick);
        if (err) return this.triggerResync(`remote batch rejected: ${err}`);
        this.emit('input', { team: e.team, batch: b });
        return;
      }
      case 'inputClosed':
        return this.applyInputClosed(e.closed);
      case 'turn': {
        const c = e.change;
        if (c.turn <= this.turn) {
          // Already applied (our own end or report, or an earlier copy of this event): it must be the same change.
          const t = this.log.turns[c.endedTurn];
          const n = this.log.turns[c.endedTurn + 1];
          if (!t || t.endTick !== c.endTick || !n || n.team !== c.team) {
            this.mismatch(c.endTick, `turn ${c.endedTurn} ended in tick ${c.endTick} for team ${c.team} on the backend, differently here`);
            this.triggerResync('a turn change differs from the local log', true);
            return;
          }
          const end = this.pendingEnds.get(c.endedTurn);
          if (end && end.forcedEnd !== undefined) this.pendingEnds.delete(c.endedTurn); // another client reported it first
          return;
        }
        if (c.endedTurn !== this.turn) return this.triggerResync(`turn change for turn ${c.endedTurn} while at turn ${this.turn}`);
        const cur = this.log.current;
        if (cur.forcedAt !== undefined) {
          // The report of the forced turn's end: nothing is missing, the ticks after its input are empty.
          if (c.endTick < cur.forcedAt) return this.triggerResync(`forced turn end ${c.endTick} precedes its closed input ${cur.forcedAt}`);
        } else if (this.isLocalTurn()) {
          // Our own turn was ended elsewhere (another window of this player): the backend's end tick wins.
          this.commitRecorded();
          const diverged = hasInputAfter(cur, c.endTick) || this.recordedThrough > c.endTick;
          this.log.truncateOpenTurn(c.endTick);
          if (this.outTurn === cur.turn) this.outTurn = null;
          if (cur.throughTick < c.endTick) return this.triggerResync(`missing input before turn end ${c.endTick}`);
          if (diverged) this.emit('rebuild', { fromTick: c.endTick + 1, reason: 'the local turn was ended elsewhere' });
        } else if (cur.throughTick < c.endTick) {
          // All input of a turn reaches the backend before its end; missing ticks mean a lost event.
          return this.triggerResync(`missing input before turn end ${c.endTick}`);
        }
        const err = this.log.endTurn(c.endedTurn, c.endTick, c.team);
        if (err) return this.triggerResync(`turn change rejected: ${err}`);
        this.pendingEnds.delete(c.endedTurn);
        this.recordedThrough = c.endTick;
        this.lastFlushTick = c.endTick;
        this.settleMoves(c.endTick);
        this.adoptOutbox();
        this.emit('turn', c);
        void this.pump();
        return;
      }
      case 'desync':
        this.desyncs.push(e.desync);
        this.emit('desync', e.desync);
        return;
      case 'ended':
        this.ended = { winnerTeam: e.winnerTeam, verified: e.verified, reason: e.reason };
        this.emit('ended', this.ended);
        return;
    }
  }

  /** The backend closed the input of `closed.turn` after `closed.tick` (a forced turn). */
  private applyInputClosed(closed: LockstepInputClosed): void {
    const E = closed.tick;
    const cur = this.log.current;
    if (closed.turn > cur.turn) return this.triggerResync(`input of turn ${closed.turn} closed while at turn ${cur.turn}`);
    if (closed.turn < cur.turn) {
      // We already moved past this turn locally: our own end (refused) or a report that settled it.
      const t = this.log.turns[closed.turn];
      if (t.forcedAt !== undefined) return; // duplicate
      const end = this.pendingEnds.get(closed.turn);
      if (!end || (t.endTick ?? -1) < E || hasInputAfter(t, E)) {
        this.pendingEnds.delete(closed.turn);
        return this.triggerResync('the backend forced a turn this client had ended', true);
      }
      this.onEndRefusedForced(closed.turn, E);
      this.emit('inputClosed', closed);
      void this.pump();
      return;
    }
    if (cur.forcedAt !== undefined) {
      if (cur.forcedAt !== E) this.triggerResync(`turn ${cur.turn} forced after ${E}, here after ${cur.forcedAt}`, true);
      return;
    }
    if (this.isLocalTurn()) {
      this.commitRecorded();
      const diverged = hasInputAfter(cur, E);
      this.log.truncateOpenTurn(E);
      if (this.outTurn === cur.turn) this.outTurn = null;
      if (cur.throughTick < E) return this.triggerResync(`the local turn was forced after ${E}, input is known through ${cur.throughTick}`, true);
      // Empty frames recorded after E are what the forced turn means: the simulation stays valid.
      if (diverged) this.emit('rebuild', { fromTick: E + 1, reason: 'the local turn was forced: input recorded after its closed input was dropped' });
    } else if (cur.throughTick !== E) {
      return this.triggerResync(`turn ${cur.turn} forced after ${E}, input known through ${cur.throughTick}`);
    }
    const err = this.log.closeInput(cur.turn, E);
    if (err) return this.triggerResync(`forced turn rejected: ${err}`, true);
    this.recordedThrough = Math.max(this.recordedThrough, E);
    this.pending = [];
    this.run = null;
    this.emit('inputClosed', closed);
    this.maybeReportForced();
  }

  /** Checks one local move start against the known turn changes (moves inside finished turns). */
  private checkMove(tick: number, team: number): void {
    this.moveStarts.delete(tick);
    const t = this.log.turnAt(tick);
    if (!t || t.endTick === null) return;
    if (tick === t.endTick) {
      const next = this.log.turns[t.turn + 1];
      if (next && next.team !== team) this.mismatch(tick, `turn ${t.turn} handed the battle to team ${next.team}, the simulation started a move of team ${team} in tick ${tick}`);
    } else if (team !== t.team) {
      this.mismatch(tick, `the simulation started a move of team ${team} in tick ${tick}, inside turn ${t.turn} of team ${t.team}`);
    }
  }

  private mismatch(tick: number, reason: string): void {
    const m = { tick, reason };
    this.mismatches.push(m);
    this.emit('mismatch', m);
  }

  private closeRun(): void {
    if (this.run) {
      this.pending.push(this.run);
      this.run = null;
    }
  }

  private emit(event: 'turn', v: LockstepTurnChange): void;
  private emit(event: 'inputClosed', v: LockstepInputClosed): void;
  private emit(event: 'desync', v: LockstepDesync): void;
  private emit(event: 'input', v: { team: number; batch: LockstepInputBatch }): void;
  private emit(event: 'ended', v: Ended): void;
  private emit(event: 'gap', v: LockstepGap): void;
  private emit(event: 'rebuild', v: LockstepRebuild): void;
  private emit(event: 'mismatch', v: LockstepMismatch): void;
  private emit(event: 'error', v: Error): void;
  private emit(event: 'halted', v: Error): void;
  private emit(event: keyof LockstepCoordinator['listeners'], v: unknown): void {
    for (const cb of this.listeners[event] as Set<Listener<unknown>>) {
      try {
        cb(v);
      } catch {
        /* listener errors must not break the coordinator */
      }
    }
  }
}
